import * as React from 'react';
import Axios from 'src/services/axios';
import { isNil, merge, isNumber, isArray, isEmpty, concat, isString, isUndefined, mapValues, omitBy } from 'lodash';
import styles from './DividedColumnDetails.styles';
import {
  DividedColumnDetailsProps,
  DividedColumnDetailsState,
  DividedColumnDetailsSection,
  DividedColumnDetailsSectionColumn,
  DividedColumnDetailsRenderer,
  ValueSelection,
  ValuePreviousSelection,
  MixedPromiseResponse,
} from './DividedColumnDetails.types';
import DividedColumnDetailsDropdown from './DividedColumnDetailsDropdown';
import { processApiParams, getUrl, getViewProm } from '../StyleEdit.utils';
import { BasicItem } from 'src/worker/pivotWorker.types';
import DependentPropDropdown from './DependentPropDropdown';
import ServiceContainer from 'src/ServiceContainer';
import Overlay from 'src/common-ui/components/Overlay/Overlay';
import InputGeneric from 'src/common-ui/components/Inputs/InputGeneric/InputGeneric';
import { classes } from 'typestyle';
import * as globalMath from 'mathjs';
import { executeCalculation } from 'src/utils/LibraryUtils/MathUtils';
import { Renderer } from 'src/utils/Domain/Renderer';
import Tooltip from '@material-ui/core/Tooltip/Tooltip';
import { maybeReturnNestData } from 'src/utils/Http/NestedDatas';
import LocalPivotServiceContext from 'src/components/StylePreview/PivotServiceContext';
import { toast } from 'react-toastify';
import { errorToLoggingPayload, logError } from 'src/services/loggingService';
import { createId } from '@paralleldrive/cuid2';
import { AppType } from 'configuration-schemas/bindings.types';
import { submitGranular } from '../StyleEditSection/StyleEditSection.utils';
import { GranularEditPayloadItem } from 'src/dao/pivotClient';
import { Suggestion } from 'src/common-ui/components/Inputs/InputSuggest/InputSuggest';
import { getValidValues } from '../StyleEditSection/StyleEditSection.client';
import { DividedColumnDetailsDropdownRenderer } from './DividedColumnDetailsDropdownRenderer';

function nullDetected(value: string[]) {
  return value.some((str) => {
    if (isNil(str)) {
      return true;
    }

    // check for 'Undefined' as a string value
    return isString(str) ? str.toLowerCase().trim() === 'undefined' : false;
  });
}

export function getTooltipTitle(value: string | string[]): string | JSX.Element {
  if (!isArray(value)) {
    return value;
  }

  if (isEmpty(value) || nullDetected(value)) {
    return ''; // don't show tooltip for empty multi-selections
  }

  return (
    <React.Fragment>
      {value.map((title) => (
        <div key={title}>{title}</div>
      ))}
    </React.Fragment>
  );
}

export default class ColumnDetails extends React.Component<DividedColumnDetailsProps, DividedColumnDetailsState> {
  state: DividedColumnDetailsState;
  math = globalMath.create(globalMath.all) as globalMath.MathJsStatic;
  static contextType = LocalPivotServiceContext;
  context!: React.ContextType<typeof LocalPivotServiceContext>;
  componentId: string = createId();

  constructor(props: DividedColumnDetailsProps) {
    super(props);

    this.state = {
      isLoading: false,
      dependentProps: {},
      dependentCalcs: {},
      openDropdownKey: null,
      dropdownOptions: {},
    };
  }

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps: DividedColumnDetailsProps) {
    if (this.props.styleId !== prevProps.styleId) {
      this.getData(true);
      // TODO: Do we still need this?
    } else if (this.props.currentRefreshCount > prevProps.currentRefreshCount) {
      const isCurrent = this.props.lastEditedSection === this.componentId;
      if (isCurrent) {
        // At the direction of management, we only want to reload 'dependent' dropdowns each time
        // dropdowns are on their own.  If a granular happens to modify these (such as from a trigger change)
        // then dropdowns will also be wrong.
        // This decision was made as it was believed extra data fetches would slow the view down
        this.getColumnValidValues(['dependentDropdown']);
      } else {
        this.getData(true);
      }
    }
  }

  redecorateData = async () => {
    const { dataApi } = this.props;
    if (!dataApi.isListData || isNil(this.state.data)) {
      this.setState({ isLoading: false });
      return;
    }
    // hardcoding this to use product: id for redecoration
    const productId = this.state.data.id;
    const newData = await ServiceContainer.pivotService.redecorate({
      coordinates: [{ product: productId }],
      defnId: dataApi.defnId,
      nestData: false,
      aggBy: dataApi.params?.aggBy.split(',') ?? [],
    });
    this.setState({
      data: newData[0],
    });
  };

  getQueue() {
    const { styleId, dataApi, configApi } = this.props;
    const listDataCall = this.context.listData;
    let dataProm: Promise<MixedPromiseResponse>;
    if (dataApi.isListData) {
      dataProm = listDataCall(dataApi.defnId, AppType.Assortment, dataApi.params).then((resp) => {
        return {
          data: {
            data: resp.flat[0],
          },
        };
      });
    } else {
      const dataUrl = getUrl(dataApi);
      dataProm = Axios.get(dataUrl);
    }

    let requests = [getViewProm(configApi), dataProm];
    if (!isNil(styleId)) {
      const encodedValue = encodeURIComponent(styleId);
      requests = concat(
        requests,
        Axios.get(`/api/member/ancestors/mapping?memberId=${encodedValue}&dimension=product`),
      );
    }

    return requests;
  }

  getData(override?: boolean) {
    // trigger the loader display
    if (this.state.isLoading && !override) return;
    this.setState(
      {
        isLoading: true,
      },
      () => {
        const queue = this.getQueue();

        const promise = Promise.all(queue).then((resp) => {
          const sectionData = maybeReturnNestData(resp[0]) as any;
          const styleData = resp[1].data.data;
          let styleMemberData = null;

          if (queue.length > 2) {
            // merge ancestor info into styleData
            const ancestorData = resp[2].data.data;
            const memberData = {};
            Object.keys(ancestorData).forEach((level: string) => {
              memberData[`member:${level}:id`] = ancestorData[level]['id'];
              memberData[`member:${level}:name`] = ancestorData[level]['name'];
            });

            styleMemberData = merge(memberData, styleData);
          }

          const sections: DividedColumnDetailsSection[] = sectionData.sections;
          const dependentProps = {};

          if (!isNil(sectionData.dependentProps)) {
            const keys = Object.keys(sectionData.dependentProps);
            keys.forEach((prop: string) => {
              const dependents = sectionData.dependentProps[prop];
              dependentProps[prop] = dependents;
            });
          }

          const dependentCalcs = {};
          if (sections) {
            sections.forEach((section) =>
              section.columns.forEach((column) => {
                if (column.dependentCalc) {
                  dependentCalcs[column.dataIndex] = column.dependentCalc;
                }
              }),
            );
          }
          const loadedData = queue.length > 2 ? styleMemberData : styleData;
          this.setState(
            {
              isLoading: false,
              sections,
              data: loadedData,
              dependentProps,
              dependentCalcs,
            },
            () => {
              // pre-fetch dropdownOptions to check validity
              this.getColumnValidValues();
            },
          );
        });

        promise.catch((err: any) => {
          toast.error('An error occurred fetching your style edit data');
          ServiceContainer.loggingService.error(`An error occurred: ${errorToLoggingPayload(err)}`, err.stack);
        });
      },
    );
  }

  getColumnValidValues(inputTypes: string[] = ['dropdown', 'dependentDropdown']) {
    const loadedData = this.state.data;
    if (!loadedData) {
      return;
    }

    this.state.sections?.forEach((section) => {
      section.columns.forEach((column) => {
        if (inputTypes.includes(column.inputType ?? '') && column.dataApi) {
          const api = processApiParams(column.dataApi, loadedData);
          const url = getUrl(api);
          const allowOpt = column.allowEmptyOption ?? true;
          const concat = column.concatOptionValues ?? false;

          getValidValues(url, allowOpt, concat)
            .then((validValues: Suggestion[]) => {
              this.setState((state) => ({
                dropdownOptions: {
                  ...state.dropdownOptions,
                  [column.dataIndex]: validValues,
                },
              }));
            })
            .catch((err) => {
              logError(`Error loading options for ${column.dataIndex}`, err);
            });
        }
      });
    });
  }

  createDividedColumnDetailSection(viewDefn: DividedColumnDetailsSection, sectionKey: string) {
    const halfLength = Math.ceil(viewDefn.columns.length / 2);
    const leftSide = this.renderColumns(viewDefn.columns.slice(0, halfLength));
    const rightSide = this.renderColumns(viewDefn.columns.slice(halfLength));

    return (
      <div key={sectionKey} className={styles.container}>
        {viewDefn.text && (
          <div className={styles.titleColumn} data-qa="section-title">
            <span>{viewDefn.text}</span>
          </div>
        )}
        <div className={styles.dataColumnWrapper} data-qa="section-data-column-wrapper">
          {leftSide}
          {rightSide}
        </div>
        <div className={styles.divider} />
      </div>
    );
  }

  renderColumns(columns: DividedColumnDetailsSectionColumn[]) {
    const { data } = this.state;
    const values: DividedColumnDetailsSectionColumn[] = [];

    columns.forEach((column) => {
      values.push({
        ...column,
        text: !isNil(data) ? data[column.dataIndex] : 'N/A',
      });
    });

    return (
      <div className={styles.dataColumn} data-qa="section-data-column">
        <div className={styles.textColumn}>{columns.map((column) => this.createLabelComponent(column))}</div>
        <div className={styles.valueColumn}>{values.map((item, index) => this.createValueComponent(item, index))}</div>
      </div>
    );
  }

  createLabelComponent = (item: DividedColumnDetailsSectionColumn) => {
    const text = item.text;
    if (item.hidden) {
      return null;
    }
    return (
      <div
        className={styles.dataItem}
        style={{ height: item.inputParams?.height }}
        key={text}
        data-qa="column-value-label"
      >
        {text}
      </div>
    );
  };

  /**
   * Get the display label(s) from the prefetched options
   */
  private getDisplayValue(
    dataIndex: string,
    rawValue: string | string[],
    multiSelect: boolean | undefined,
  ): string | string[] {
    const opts: Suggestion[] = this.state.dropdownOptions[dataIndex] || [];

    // look up matching options based on multi or single
    if (multiSelect) {
      const ids = Array.isArray(rawValue)
        ? rawValue
        : // default empty values "{}" to empty array,
          // an empty value run through array split below produces [""]
          // which will cause a blank chip to be rendered
          rawValue === '{}'
          ? []
          : rawValue.replace('{', '').replace('}', '').split(',');

      const matches = ids.map((id) => {
        const match = opts.find((opt) => String(opt.value) === String(id));
        return match ? match.label : String(id);
      });
      return matches;
    } else {
      const id = String(rawValue ?? '');
      const match = opts.find((opt) => String(opt.value) === id);
      return match ? match.label : id;
    }
  }

  showEditor = (rendererKey: string) => this.setState({ openDropdownKey: rendererKey });
  showRenderer = () => this.setState({ openDropdownKey: null });

  createValueComponent = (item: DividedColumnDetailsSectionColumn, componentIndex: number) => {
    const { styleId } = this.props;
    const {
      text,
      dataIndex,
      dataApi,
      renderer,
      inputType,
      editable,
      asCsv,
      multiSelect,
      valueType,
      mandatory,
      hidden,
      allowEmptyOption,
      concatOptionValues,
      inputParams,
      disabledBy,
    } = item;
    const { data, dropdownOptions } = this.state;
    const value = !isNil(data) ? data[dataIndex] : '';
    const key = `${dataIndex}-${componentIndex}`;

    if (hidden) return null;

    if (!editable && inputType !== 'dependentDropdown') {
      const formattedText = this.formatText(text, renderer);
      return (
        <div className={classes(styles.dataItem, styles.dataText)} key={key} data-qa="non-editable-column-value">
          <div className={styles.dataText}>{formattedText}</div>
        </div>
      );
    }

    let isDisabled = false;
    if (!isNil(disabledBy) && !isNil(data) && !isNil(data[disabledBy])) {
      isDisabled = true;
    }
    const rawValue = value;
    // check for empty vals
    const isEmptySingle = !multiSelect && (rawValue == null || rawValue === '');
    const isEmptyMulti = multiSelect && Array.isArray(rawValue) && rawValue.length === 0;
    // if the field is marked mandatory, it’s “missing”
    const isMissing = mandatory && (isEmptySingle || isEmptyMulti);
    // grab the pre-fetched list of valid options for this column
    const opts: Suggestion[] = dropdownOptions[dataIndex] || [];
    const isUnmapped =
      !multiSelect &&
      typeof rawValue === 'string' &&
      opts.length > 0 &&
      !opts.some((opt) => String(opt.value) === rawValue);

    const isInvalid = isMissing ?? isUnmapped;

    switch (inputType) {
      case 'dropdown': {
        if (!dataApi) return <div />;
        const allowEmpty = isNil(allowEmptyOption) ? true : allowEmptyOption;
        const concatValues = isNil(concatOptionValues) ? false : concatOptionValues;
        const displayValue = this.getDisplayValue(dataIndex, rawValue, multiSelect);
        const tooltipTitle = !multiSelect ? '' : getTooltipTitle(displayValue);
        const isActive = this.state.openDropdownKey === key;
        const processedDataApi = processApiParams(dataApi, data ?? {});

        return isDisabled || !isActive ? (
          <DividedColumnDetailsDropdownRenderer
            id={key}
            key={key}
            displayValue={displayValue}
            inputParams={inputParams}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            onClick={this.showEditor}
            tooltip={tooltipTitle}
          />
        ) : (
          <Tooltip
            className={styles.dataItem}
            data-qa="real-multiselect-tooltip"
            key={key}
            title={tooltipTitle}
            placement="right"
          >
            <DividedColumnDetailsDropdown
              dataQa={`select-${key}`}
              styleId={styleId}
              dataApi={processedDataApi}
              asCsv={asCsv}
              multiSelect={multiSelect}
              fullWidthLabels={true}
              value={value}
              handleDropdownChange={(sel) => this.handleDropdownChange(sel, item)}
              allowEmptyOption={allowEmpty}
              concatOptionValues={concatValues}
              disabled={isDisabled}
              inputParams={inputParams}
              initiallyOpen={true}
              onClose={this.showRenderer}
            />
          </Tooltip>
        );
      }

      case 'dependentDropdown': {
        if (!dataApi) return <div />;
        const processedDataApi = processApiParams(dataApi, data ?? {});
        const allowEmpty = isNil(allowEmptyOption) ? true : allowEmptyOption;
        const concatValues = isNil(concatOptionValues) ? false : concatOptionValues;
        const isActive = this.state.openDropdownKey === key;
        const displayValue = this.getDisplayValue(dataIndex, rawValue, multiSelect);

        return isDisabled || !isActive ? (
          <DividedColumnDetailsDropdownRenderer
            key={key}
            id={key}
            displayValue={displayValue}
            inputParams={inputParams}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            onClick={this.showEditor}
            tooltip={''}
          />
        ) : (
          <DependentPropDropdown
            key={key}
            value={value}
            styleId={styleId}
            dataApi={processedDataApi}
            handleDropdownChange={(selection) => this.handleDropdownChange(selection, item)}
            dataQa={`select-${key}`}
            mandatory={mandatory}
            allowEmptyOption={allowEmpty}
            concatOptionValues={concatValues}
            editable={editable && !isDisabled}
            initiallyOpen={true}
            onClose={() => {
              this.setState({ openDropdownKey: null });
            }}
          />
        );
      }

      case 'text': {
        const formattedText = this.formatText(value, renderer);

        return (
          <InputGeneric
            key={key}
            className={classes(styles.dataItem, styles.dataTextInput)}
            editable={true}
            autoFocus={false}
            value={formattedText}
            valid={true}
            disabled={isDisabled}
            onBlur={(newText: string) => {
              let result;
              if (valueType === 'number') {
                // TODO: make a configurable parser for this...
                result = parseFloat(newText.replace(/^\$*/g, ''));
              } else {
                result = newText;
              }
              const selection = {
                // mimic dropdown selection structure
                value: result,
                label: newText,
              };
              this.handleDropdownChange(selection, item);
            }}
          />
        );
      }
      default:
        return null;
    }
  };

  formatText(text: string, renderer: DividedColumnDetailsRenderer | undefined) {
    const numericValue = Number(text); // below expects to work with number types only
    const valueRenderer = renderer ?? '';
    const rendererFn = Renderer[valueRenderer];
    return !isNil(rendererFn) && !isNaN(numericValue) ? rendererFn(numericValue) : text;
  }

  handleDropdownChange = (selection: ValueSelection, item: DividedColumnDetailsSectionColumn) => {
    // if the selection is undefined, don't go through this (dependent dropdowns without options)
    if (isUndefined(selection)) return;
    const { styleId } = this.props;
    const { data, dependentCalcs } = this.state;
    const { dataIndex } = item;
    let updatedData: BasicItem | undefined;
    let prevSelection = {};

    // assume data update will succeed...on failure 'rollback' to original values
    const nameDataIndex = dataIndex.replace(':id', ':name');
    const updatedValue = isNumber(selection.value) ? (!isNaN(selection.value) ? selection.value : 0) : selection.value;

    if (!isNil(data)) {
      // update attribute (id and name fields)
      // assuming at this level we will never be updating id, name, description on style
      // only members/attributes now above

      // store prev selection for rollback
      prevSelection = {
        [dataIndex]: data[dataIndex],
        [nameDataIndex]: data[nameDataIndex],
      };

      updatedData = { ...data };
      updatedData[dataIndex] = updatedValue;
      updatedData[nameDataIndex] = selection.label;
    }

    // check if dataindex is used in any dependent calculations, and if it is, update those calcs
    const calcKeys = Object.keys(dependentCalcs);
    if (calcKeys.length > 0 && updatedData) {
      calcKeys.forEach((key) => {
        let updateCalculation = false;
        const calcObj = dependentCalcs[key];
        const params = calcObj.params;

        if (params) {
          for (const p in params) {
            if (dataIndex === params[p] || nameDataIndex === params[p]) {
              updateCalculation = true;
            }
          }
        }
        if (updateCalculation && updatedData) {
          const getDataFromKey = (key2: string) => {
            if (!updatedData) return { rowNodeFound: false, data: undefined };
            return {
              rowNodeFound: true,
              data: updatedData[key2],
            };
          };
          const newValue = executeCalculation(this.math, calcObj, getDataFromKey);
          const keyName = key.replace(':id', ':name');
          updatedData[key] = newValue;
          updatedData[keyName] = newValue;
          this.saveData(styleId, key, newValue);
        }
      });
    }

    this.setState(
      {
        data: updatedData,
      },
      () => this.saveData(styleId, dataIndex, updatedValue, prevSelection),
    );
  };

  saveData(styleId: string, dataIndex: string, value: string | number, _prevSelection?: ValuePreviousSelection) {
    const { updateCoordinateMap } = this.props;
    const row = this.state.data || {};

    // build coords, either from the passed‐in map or default to product:id
    const coords = updateCoordinateMap
      ? omitBy(
          mapValues(updateCoordinateMap, (field) => row[field]),
          isNil,
        )
      : { product: styleId }; // fallback to what /product endpoint used
    // This fun mess removes that silly wrapped colon stuff (eg: attribute:<x>:id)
    // This is the more concise version: .replace(/(?:^.*?:)?([^:]*)(?::.*)?/, '$1')
    const formattedDataIndex = dataIndex.replace(/(member|attribute):/, '').replace(/:(id|name|description)/, '');
    // build payload item
    const payload: GranularEditPayloadItem = {
      coordinates: coords,
      [formattedDataIndex]: value,
    };

    submitGranular([payload])
      .then(async () => {
        this.context.clearPivotCache();
        await this.redecorateData();
        // re-introduce refreshSections to update the UI
        this.props.refreshSections(this.componentId);
      })
      .catch((e) => {
        toast.error('An error occured updating your style');
        ServiceContainer.loggingService.error('Error in granular update', e.stack);
      });
  }

  render() {
    if (!this.props.expanded) {
      // in the mounted but in un-expanded state, renderer nothing to save resources
      return <React.Fragment></React.Fragment>;
    }
    if (this.state.isLoading) {
      return (
        <Overlay type="loading" visible={true} fitParent={true} qaKey={'StyleEditSectionDividedColumnDetailsOverlay'} />
      );
    }

    const { sections } = this.state;
    if (isNil(sections)) {
      return null;
    }

    const columns = sections.map((section, index) => {
      return this.createDividedColumnDetailSection(section, `${section.text}-${index}`);
    });
    return (
      <div className={styles.outerContainer} data-qa="column-details-container">
        {columns}
      </div>
    );
  }
}
